import React, { FC, useMemo } from 'react';
import { Link } from 'react-router-dom';
import * as types from '../../store/constants';
import { PRODUCT_CLASS_CAMPAIGN } from '../../store/constants';
import ProductCover from '../../shared/common/ProductCover';
import { CAMPAIGN_PRODUCTS, isFeatureEnabled } from '../../shared/utils/togglz';
import ProductCampaignLabel from '../../shared/product/ProductCampaignLabel';
import Utils from '../../shared/utils';
import ProductAuthors from '../../shared/product/ProductAuthors';
import ProductNarrators from '../../shared/product/ProductNarrators';
import ProductLanguage from '../../shared/product/ProductLanguage';
import ProductUsageConditions from '../../shared/product/ProductUsageConditions';
import { concreteLoanFormatNameToKey, LOANFORMAT_ALL_OBJECT } from '../../store/loanFormat/actions';
import classNames from 'classnames';
import OrderStatusButton from '../../shared/common/OrderStatusButton';
import { InvoiceOut } from '../../store/api';
import { FetchOrderProductsLineItem } from '../../api/orders/fetchOrder';
import FormatUtils from '../../shared/utils/utils-format';
import { CopiesListDropdown } from '../../shared/product/CopiesListDropdown';
import { CopiesList } from '../../shared/product/CopiesList';
import { useSelector } from 'react-redux';
import { AppState } from '../../store/appState';
import { useOrderLineItemCopies } from './useOrderLineItemCopies';
import { UsageCondition } from '../../../models/Product';
import { SubscriptionPeriodComponent } from '../checkout/CheckoutListItem';
import { formatLoanFormat } from '../../shared/utils/formatLoanFormat';
import { mapLoanFormat } from '../../../models/LoanFormat';
import { useTranslation } from 'react-i18next';
import { mapProductOrderStatus } from '../../shared/common/translationMappers/orderStatusMapper';
import FormatCurrency from '../../shared/utils/formatCurrency';

type Props = {
    readonly orderId: string;
    readonly product: FetchOrderProductsLineItem;
    readonly onDownloadInvoice: (invoice: InvoiceOut) => void;
};

export const OrdersDetailsProductListItem: FC<Props> = ({ orderId, product, onDownloadInvoice }) => {
    const { t } = useTranslation();

    const statusRequiresDate: string[] | undefined = [
        'PRE_ORDERED_EXPECTED',
        'PRE_ORDERED_DELAYED',
        'PRE_ORDERED_EMBARGO'
    ];

    const buildStatusLine = () => {
        const displayBadge = typeof product.status !== 'undefined' && product.status.length > 0;
        const statuses = product.status?.map((productStatus, index) => {
            const statusKey = mapProductOrderStatus(productStatus.statusKey);
            return (
                <OrderStatusButton
                    onDownloadInvoice={onDownloadInvoice}
                    key={index}
                    status={t(`shop.orders.orderStatus.${statusKey}`, {
                        defaultValue: productStatus.status,
                        /*TODO [i18n] use datetime intl function */
                        date: statusRequiresDate.includes(productStatus.statusKey!)
                            ? productStatus.preOrderShipDate
                            : null
                    })}
                    amount={productStatus.amount}
                    invoice={productStatus.invoice}
                    displayBadge={displayBadge}
                    salesStatus={product.salesStatus}
                />
            );
        });
        return <div className="status-line">{statuses}</div>;
    };

    const isCampaignProduct = product.productClass === PRODUCT_CLASS_CAMPAIGN;

    const packshotCssClass = classNames(
        'product-list-cell',
        'packshot',
        'checkout',
        'product',
        `${mapLoanFormat(product.loanFormat)?.toLowerCase()}-checkout`
    );

    const statusLine = product.status && product.status.length ? buildStatusLine() : null;
    const loanFormatKey = concreteLoanFormatNameToKey(product.loanFormat);

    return (
        <div className="product-list-row">
            <div className={packshotCssClass}>
                <Link to={`${types.PATH_PRODUCTS}/${product.id}`}>
                    <ProductCover minSizeInPx={180} product={product} />
                </Link>
            </div>
            <div className="tc product-list-cell tcw-30">
                {isCampaignProduct && isFeatureEnabled(CAMPAIGN_PRODUCTS) && (
                    <ProductCampaignLabel loanFormat={mapLoanFormat(product.loanFormat)!} />
                )}
                <p className="series">{FormatUtils.truncate(product.seriesName)}</p>
                <p title={product.title} className="label">
                    <Link to={`${types.PATH_PRODUCTS}/${product.id}`}>
                        <strong>{Utils.truncate(product.title)}</strong>
                    </Link>
                </p>
                <ProductAuthors product={product} loanFormatKey={loanFormatKey} />
                <ProductNarrators product={product} loanFormatKey={loanFormatKey} />
                <p>
                    {product.isbn13} &ndash; {formatLoanFormat(product.loanFormat, 'singular')}
                </p>

                <ProductLanguage product={product} loanFormatKey={loanFormatKey} label={product.language || ''} />
            </div>
            <div className="tc product-list-cell tcw-15 a-right">
                <SubscriptionPeriodComponent startDate={product.planStartDate} endDate={product.planEndDate} />

                <ProductUsageConditions
                    product={product}
                    showDetails={true}
                    loanFormatKey={LOANFORMAT_ALL_OBJECT.key}
                    showSelectedPrice={true}
                />
            </div>
            <div className="tc product-list-cell tcw-10 a-right">
                <p>
                    {t('numberFormat.numberWithCurrencySymbol', {
                        val: FormatCurrency.convertIntegerToCurrencyFormat(product.price?.copyBasePrice),
                        currencySymbol: FormatCurrency.mapCurrencyToSymbol(product.price?.currency)
                    })}
                </p>
            </div>
            <div className="tc product-list-cell tcw-5 a-right">
                <p>{product.price?.copyAmount}</p>
            </div>
            <div className="tc product-list-cell tcw-10 a-right">
                <div>
                    {(product.price?.savedValue ? '- ' : '') +
                        t('numberFormat.numberWithCurrencySymbol', {
                            val: FormatCurrency.convertIntegerToCurrencyFormat(product.price?.savedValue),
                            currencySymbol: FormatCurrency.mapCurrencyToSymbol(product.price?.currency)
                        })}
                </div>
                <div className="cold-grey">
                    {product.price?.savedPercentage
                        ? `(${t('numberFormat.numberWithoutDecimalPoint', {
                              val: Math.round((product.price?.savedPercentage ?? 0) / 100)
                          })}%)`
                        : null}
                </div>
            </div>
            <div className="tc product-list-cell tcw-10 a-right">
                <p>
                    {t('numberFormat.numberWithCurrencySymbol', {
                        val: FormatCurrency.convertIntegerToCurrencyFormat(product.price?.netValue),
                        currencySymbol: FormatCurrency.mapCurrencyToSymbol(product.price?.currency)
                    })}
                </p>
            </div>
            <div className="tc product-list-cell tcw-10 ">
                <OrderDetailsProductListItemCopiesListContainer orderId={orderId} product={product} />
                {statusLine}
            </div>
        </div>
    );
};

type OrderDetailsProductListItemCopiesListProps = {
    readonly orderId: string;
    readonly product: FetchOrderProductsLineItem;
};

const OrderDetailsProductListItemCopiesListContainer: FC<OrderDetailsProductListItemCopiesListProps> = props => {
    return (
        <CopiesListDropdown label="Details" className="fl-right">
            <OrderDetailsProductListItemCopiesList {...props} />
        </CopiesListDropdown>
    );
};

const OrderDetailsProductListItemCopiesList: FC<OrderDetailsProductListItemCopiesListProps> = ({
    orderId,
    product: { id: productId, loanFormat, price }
}) => {
    const { lendingModel, usageLimit, termLimit } = price ?? {
        lendingModel: undefined,
        usageLimit: undefined,
        termLimit: undefined
    };

    const usageCondition: UsageCondition = useMemo(
        () => ({ lendingModel, usageLimit, termLimit }),
        [lendingModel, usageLimit, termLimit]
    );

    const { taxName } = useSelector((state: AppState) => state.authentication.spoc);

    const { copies, status } = useOrderLineItemCopies({
        orderId,
        productId,
        usageCondition
    });

    return (
        <CopiesList
            loanFormat={loanFormat}
            netValue={price?.netValue}
            currency={price?.currency}
            taxName={taxName}
            copies={copies}
            loading={status === 'loading'}
            disabled={true}
        />
    );
};
